import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Check, Clock, MapPin, Shield, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import MinimalHeader from "@/components/MinimalHeader";
import MinimalFooter from "@/components/MinimalFooter";
import ProjectWizard from "@/components/ProjectWizard";
import { cities } from "@/data/cities";
import { blogPosts } from "@/data/blog";

const scrollToForm = () => {
  document.querySelector("#city-form")?.scrollIntoView({ behavior: "smooth", block: "center" });
};

const statValueStyle = { fontFamily: "var(--font-heading)" };

const TrustBar = () => (
  <div className="border-y border-border/50 bg-card/30">
    <div className="max-w-5xl mx-auto px-6 py-3 flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-xs sm:text-sm text-muted-foreground">
      <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-primary" strokeWidth={3} /> 100% sur mesure</span>
      <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-primary" strokeWidth={3} /> Livré en 2 semaines</span>
      <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-primary" strokeWidth={3} /> À partir de 49,90€/mois</span>
      <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-primary" strokeWidth={3} /> Vous êtes propriétaire</span>
    </div>
  </div>
);

const Hero = ({ city }) => (
  <section className="pt-28 pb-12 md:pt-32 md:pb-16 px-6 relative overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-transparent to-transparent pointer-events-none" />
    <div className="max-w-6xl mx-auto relative z-10 grid lg:grid-cols-5 gap-10 items-start">
      <div className="lg:col-span-3 pt-4">
        <div className="inline-flex items-center gap-2 bg-primary/10 text-primary text-xs font-semibold uppercase tracking-widest px-3 py-1.5 rounded-full mb-6">
          <MapPin className="w-3.5 h-3.5" />
          <span>{city.name} · {city.department} · {city.region}</span>
        </div>
        <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight leading-[1.05] mb-6">
          Création de site internet{" "}
          <span className="text-primary">à {city.name}</span>.
        </h1>
        <p className="text-lg text-muted-foreground leading-relaxed mb-8 max-w-xl">
          {city.intro}
        </p>

        <div className="grid sm:grid-cols-3 gap-3 mb-8 max-w-xl">
          {[
            { icon: Zap, label: "Livraison rapide", sub: "2 semaines" },
            { icon: Shield, label: "Zéro template", sub: "Unique" },
            { icon: Clock, label: "Réponse", sub: "Sous 48h" },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-3 p-3 rounded-xl border border-border/60 bg-card/50">
              <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center flex-shrink-0">
                <item.icon className="w-4 h-4 text-primary" strokeWidth={2.5} />
              </div>
              <div>
                <p className="text-sm font-semibold leading-tight">{item.label}</p>
                <p className="text-xs text-muted-foreground">{item.sub}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={scrollToForm}
            size="lg"
            className="rounded-full bg-primary hover:bg-primary/90 h-12 px-8 text-base font-semibold shadow-xl shadow-primary/20 group"
          >
            Obtenir mon devis gratuit
            <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Button>
          <div className="flex items-center gap-1 px-2">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 fill-primary text-primary" />
              ))}
            </div>
            <span className="text-sm text-muted-foreground ml-1">Clients satisfaits</span>
          </div>
        </div>
      </div>

      <div id="city-form" className="lg:col-span-2 lg:sticky lg:top-24">
        <ProjectWizard
          source={`city-${city.slug}-wizard`}
          subjectPrefix={`[${city.name}]`}
        />
      </div>
    </div>
  </section>
);

const Stats = ({ city }) => (
  <section className="py-12 md:py-16 px-6 border-t border-border/50">
    <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
      <div className="bg-card border border-border rounded-2xl p-5 text-center">
        <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">À partir de</p>
        <p className="text-3xl md:text-4xl font-semibold text-primary leading-none" style={statValueStyle}>
          49,90€<span className="text-sm font-medium text-muted-foreground">/mois</span>
        </p>
      </div>
      {[
        { value: "2 sem.", label: "Livraison moyenne" },
        { value: "48h", label: "Devis garanti" },
        { value: city.department, label: "Zone couverte" },
      ].map((stat) => (
        <div key={stat.label} className="bg-card border border-border rounded-2xl p-5 text-center">
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">{stat.label}</p>
          <p className="text-2xl md:text-3xl font-semibold text-primary leading-none" style={statValueStyle}>{stat.value}</p>
        </div>
      ))}
    </div>
  </section>
);

const ValueProps = ({ city }) => {
  const points = [
    {
      title: `Conçu pour les entreprises de ${city.name}`,
      body: city.why,
    },
    {
      title: "Performant et bien référencé",
      body: `Code propre, chargement rapide, SEO travaillé pour apparaître sur les recherches locales "${city.name}". Votre site est un outil commercial, pas une carte de visite.`,
    },
    {
      title: "Tout inclus, sans apport",
      body: "Hébergement, maintenance, mises à jour, support — tout est compris dans le mensuel. Pas d'investissement initial, pas de mauvaise surprise.",
    },
  ];

  return (
    <section className="py-16 md:py-20 px-6 border-t border-border/50">
      <div className="max-w-5xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-xs font-bold uppercase tracking-widest text-primary mb-3">
            Pourquoi Lacrea à {city.name}
          </p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
            Un site pro, enfin accessible.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {points.map((p) => (
            <div key={p.title} className="p-6 rounded-2xl border border-border bg-card">
              <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center mb-4">
                <Check className="w-5 h-5 text-primary" strokeWidth={2.5} />
              </div>
              <h3 className="font-semibold text-lg mb-2">{p.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Process = ({ city }) => {
  const steps = [
    { n: "1", title: "On échange", body: `Appel de 30 min pour comprendre votre activité à ${city.name}, vos clients, vos objectifs.` },
    { n: "2", title: "On conçoit et on code", body: "Maquettes validées sous 1 semaine, développement sur mesure derrière." },
    { n: "3", title: "On livre et on accompagne", body: "Mise en ligne, formation, et un mois de suivi inclus pour ajuster." },
  ];

  return (
    <section className="py-16 md:py-20 px-6 border-t border-border/50">
      <div className="max-w-5xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-xs font-bold uppercase tracking-widest text-primary mb-3">
            Comment ça se passe
          </p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
            Un processus simple, sans jargon.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {steps.map((s) => (
            <div key={s.n}>
              <div className="text-4xl font-bold text-primary mb-3">{s.n}</div>
              <h3 className="font-semibold text-lg mb-2">{s.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Resources = () => (
  <section className="py-16 md:py-20 px-6 border-t border-border/50">
    <div className="max-w-5xl mx-auto">
      <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-8">
        Ressources utiles
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {blogPosts.slice(0, 3).map((post) => (
          <Link
            key={post.slug}
            to={`/blog/${post.slug}`}
            className="bg-card border border-border rounded-2xl overflow-hidden hover:border-primary/30 transition-colors group"
          >
            <img src={post.image} alt={post.title.fr} className="w-full h-40 object-cover" loading="lazy" />
            <div className="p-4">
              <span className="text-xs text-primary font-medium">{post.category}</span>
              <h3 className="text-sm font-semibold mt-1 group-hover:text-primary transition-colors">{post.title.fr}</h3>
            </div>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

const NearbyCities = ({ city }) => (
  <section className="py-12 md:py-16 px-6 border-t border-border/50">
    <div className="max-w-5xl mx-auto">
      <h2 className="text-xl font-bold tracking-tight mb-6">
        Nous intervenons aussi à proximité
      </h2>
      <div className="flex flex-wrap gap-3">
        {cities
          .filter((c) => c.slug !== city.slug)
          .slice(0, 8)
          .map((c) => (
            <Link
              key={c.slug}
              to={`/creation-site-internet/${c.slug}`}
              className="text-sm px-4 py-2 rounded-full bg-card border border-border hover:border-primary/30 hover:text-primary transition-colors"
            >
              {c.name}
            </Link>
          ))}
      </div>
    </div>
  </section>
);

const FinalCTA = ({ city }) => (
  <section className="py-16 md:py-20 px-6 border-t border-border/50">
    <div className="max-w-3xl mx-auto text-center">
      <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
        Prêt à lancer votre projet à {city.name} ?
      </h2>
      <p className="text-muted-foreground text-lg mb-8">
        Décrivez votre activité en quelques lignes. Réponse sous 48h, devis gratuit et sans engagement.
      </p>
      <Button
        onClick={scrollToForm}
        size="lg"
        className="rounded-full bg-primary hover:bg-primary/90 h-12 px-8 text-base font-semibold shadow-xl shadow-primary/20 group"
      >
        Obtenir mon devis gratuit
        <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
      </Button>
    </div>
  </section>
);

const StickyMobileCTA = () => (
  <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 p-3 bg-background/95 backdrop-blur-md border-t border-border/50">
    <Button
      onClick={scrollToForm}
      className="w-full rounded-full bg-primary hover:bg-primary/90 h-12 text-base font-semibold"
    >
      Obtenir mon devis gratuit
      <ArrowRight className="ml-2 w-4 h-4" />
    </Button>
  </div>
);

const CityLandingPage = () => {
  const { city: citySlug } = useParams();
  const city = cities.find((c) => c.slug === citySlug);

  if (!city) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Page introuvable</h1>
          <Link to="/" className="text-primary hover:underline">Retour à l'accueil</Link>
        </div>
      </div>
    );
  }

  const title = `Création de Site Internet à ${city.name} | À partir de 49,90€/mois — Lacrea`;
  const description = `Agence web à ${city.name} (${city.department}). Sites internet sur mesure pour PME et entrepreneurs. À partir de 49,90€/mois, sans apport. Devis gratuit 48h.`;
  const path = `/creation-site-internet/${city.slug}`;
  const url = `https://lacrea.fr${path}`;

  return (
    <div className="min-h-screen bg-background text-foreground pb-20 lg:pb-0">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={city.keywords} />
        <link rel="canonical" href={url} />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={url} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:site_name" content="Lacrea" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            name: "Lacrea",
            description: `Création de sites internet professionnels à ${city.name}`,
            url,
            email: "contact@lacrea.fr",
            areaServed: { "@type": "City", name: city.name },
            address: {
              "@type": "PostalAddress",
              addressLocality: city.name,
              addressRegion: city.region,
              addressCountry: "FR",
            },
            priceRange: "49,90€ - 149€/mois",
            serviceType: [
              "Création de site internet",
              "Design UI/UX",
              "Développement web",
              "E-commerce",
              "SEO local",
            ],
          })}
        </script>
      </Helmet>

      <MinimalHeader />

      <main>
        <Hero city={city} />
        <TrustBar />
        <Stats city={city} />
        <ValueProps city={city} />
        <Process city={city} />
        <Resources />
        <NearbyCities city={city} />
        <FinalCTA city={city} />
      </main>

      <MinimalFooter />
      <StickyMobileCTA />
    </div>
  );
};

export default CityLandingPage;
